import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle, XCircle, Upload,
  Eye, Code2, PencilLine, Plus, Trash2, GripVertical, Lock,
  FileText, Loader2, PencilRuler,
} from 'lucide-react'
import { supabase } from '../../config/supabase'
import type { Post, PostSection, CodeBlock } from './posts'
import PostRenderer from './PostRenderer'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string

// ─── AI prompt ────────────────────────────────────────────────────────────────

const PROMPT_TEMPLATE = `You are writing a learning post for a portfolio blog. The author is a BioScience student now building software at a startup. The tone is direct and honest — no hype, no em dashes, no filler phrases like "dive into" or "let's explore". Write like a person who figured something out and is explaining it plainly.

Return ONLY valid JSON in exactly this shape. No markdown fences, no extra text.

{
  "slug": "kebab-case-url-slug",
  "title": "Post title here",
  "subtitle": "One sentence that says what the post is actually about.",
  "date": "{today's date in the format of DD Mon YYYY (Day)}",
  "readTime": "5 min",
  "intro": "One or two sentences. What you understood wrong, or what confused you before this clicked.",
  "sections": [
    {
      "heading": "Section heading",
      "body": [
        "First paragraph as a plain string.",
        "Second paragraph as a plain string. Each paragraph is a separate string in the array."
      ],
      "code": {
        "language": "tsx",
        "label": "optional-filename.tsx",
        "code": "// code goes here\\nconst x = 1"
      },
      "note": "Optional. A short callout — a key distinction, a gotcha, or something easy to mix up. Leave this field out entirely if not needed."
    }
  ],
  "closing": "One or two sentences. The thing that ties it together — the insight that makes the whole post make sense."
}

RULES:
- slug: lowercase, hyphens only, no special chars
- date: "DD Mon YYYY (Day)" format e.g. "15 Mar 2026 (Sun)"
- readTime: estimate honestly e.g. "4 min"
- body: always an array of strings, even if just one paragraph
- code: omit the entire "code" key if the section has no code example
- note: omit the entire "note" key if there is no callout
- language values for code blocks: "tsx", "ts", "js", "jsx", "css", "sql", "bash", "json", "html"
- No em dashes (—). Use a comma or a new sentence instead.
- No phrases like: "let's dive in", "in this post", "it's important to note", "key takeaway"
- Write the closing as a real observation, not a summary`

const CODE_LANGS = ['tsx', 'ts', 'js', 'jsx', 'css', 'sql', 'bash', 'json', 'html']

// ─── Auto-resize textarea (grows with content, no inner scroll) ─────────────────

function useAutoResizeTextarea(ref: React.RefObject<HTMLTextAreaElement | null>, value: string, enabled = true) {
  useLayoutEffect(() => {
    if (!enabled) return
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value, enabled, ref])
}

// Standalone auto-resize textarea for use in SectionEditor (multiple instances)
const AutoResizeTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void }
> = ({ value, onChange, className = '', ...rest }) => {
  const ref = useRef<HTMLTextAreaElement>(null)
  useAutoResizeTextarea(ref, value)
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      className={`min-h-[2.5rem] resize-none overflow-hidden ${className}`}
      {...rest}
    />
  )
}

// ─── Reusable inline field ────────────────────────────────────────────────────

const Field: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
  mono?: boolean
  multiline?: boolean
  placeholder?: string
  rows?: number
  autoResize?: boolean
}> = ({ label, value, onChange, mono, multiline, placeholder, rows = 3, autoResize = false }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useAutoResizeTextarea(textareaRef, value, multiline && autoResize)

  return (
    <div className="space-y-1.5 min-w-0">
      <label className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase">
        {label}
      </label>
      {multiline ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={`w-full min-w-0 min-h-[4rem] bg-jax-bg border border-jax-navy rounded-lg px-3 py-2.5 text-xs sm:text-sm text-white leading-relaxed outline-none focus:border-jax-link transition-colors duration-150 overflow-hidden ${mono ? 'font-mono text-[11px] sm:text-[13px]' : ''} ${autoResize ? 'resize-none' : 'resize-y'}`}
          spellCheck={!mono}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full min-w-0 bg-jax-bg border border-jax-navy rounded-lg px-3 py-2.5 text-[11px] sm:text-sm text-white outline-none focus:border-jax-link transition-colors duration-150 ${mono ? 'font-mono text-[11px] sm:text-[13px]' : ''}`}
          spellCheck={false}
        />
      )}
    </div>
  )
}

// ─── Section editor ───────────────────────────────────────────────────────────

const SectionEditor: React.FC<{
  section: PostSection
  index: number
  total: number
  onChange: (s: PostSection) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}> = ({ section, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const [codeOpen, setCodeOpen] = useState(!!section.code)
  const [noteOpen, setNoteOpen] = useState(!!section.note)

  const setBody = (i: number, val: string) => {
    const next = [...section.body]
    next[i] = val
    onChange({ ...section, body: next })
  }

  const addPara = () => onChange({ ...section, body: [...section.body, ''] })

  const removePara = (i: number) => {
    const next = section.body.filter((_, j) => j !== i)
    onChange({ ...section, body: next.length ? next : [''] })
  }

  const toggleCode = () => {
    if (codeOpen) {
      const { code: _, ...rest } = section as PostSection & { code?: CodeBlock }
      onChange(rest)
      setCodeOpen(false)
    } else {
      onChange({ ...section, code: { language: 'tsx', label: '', code: '' } })
      setCodeOpen(true)
    }
  }

  const toggleNote = () => {
    if (noteOpen) {
      const { note: _, ...rest } = section as PostSection & { note?: string }
      onChange(rest)
      setNoteOpen(false)
    } else {
      onChange({ ...section, note: '' })
      setNoteOpen(true)
    }
  }

  const setCode = (patch: Partial<CodeBlock>) =>
    onChange({ ...section, code: { ...(section.code ?? { language: 'tsx', label: '', code: '' }), ...patch } })

  return (
    <div className="border border-jax-navy rounded-xl bg-jax-surface p-4 sm:p-5 space-y-4 min-w-0">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono text-jax-subtle tracking-widest uppercase">
          Section {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={index === 0}
            className="p-1.5 text-jax-subtle hover:text-white disabled:opacity-20 transition-colors" title="Move up">
            <GripVertical className="w-3.5 h-3.5 rotate-90" />
          </button>
          <button onClick={onMoveDown} disabled={index === total - 1}
            className="p-1.5 text-jax-subtle hover:text-white disabled:opacity-20 transition-colors" title="Move down">
            <GripVertical className="w-3.5 h-3.5 -rotate-90" />
          </button>
          <button onClick={onRemove}
            className="p-1.5 text-jax-subtle hover:text-red-400 transition-colors ml-1" title="Remove section">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Heading */}
      <Field
        label="Heading"
        value={section.heading}
        onChange={(v) => onChange({ ...section, heading: v })}
        placeholder="Section heading"
      />

      {/* Body paragraphs */}
      <div className="space-y-2">
        <label className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase">
          Body paragraphs
        </label>
        {section.body.map((para, i) => (
          <div key={i} className="flex gap-2 items-start">
            <AutoResizeTextarea
              value={para}
              onChange={(e) => setBody(i, e.target.value)}
              placeholder={`Paragraph ${i + 1}`}
              className="flex-1 bg-jax-bg border border-jax-navy rounded-lg px-3 py-2.5 text-xs sm:text-sm text-white leading-relaxed outline-none focus:border-jax-link transition-colors duration-150"
            />
            <button onClick={() => removePara(i)}
              className="mt-2 p-1.5 text-jax-subtle hover:text-red-400 transition-colors shrink-0" title="Remove paragraph">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button onClick={addPara}
          className="flex items-center gap-1.5 text-[11px] font-mono text-jax-subtle hover:text-jax-link transition-colors">
          <Plus className="w-3 h-3" /> add paragraph
        </button>
      </div>

      {/* Code block toggle */}
      <div className="space-y-3">
        <button onClick={toggleCode}
          className={`flex items-center gap-1.5 text-[11px] font-mono transition-colors ${codeOpen ? 'text-jax-link' : 'text-jax-subtle hover:text-jax-link'}`}>
          <Code2 className="w-3 h-3" />
          {codeOpen ? 'remove code block' : '+ add code block'}
        </button>
        {codeOpen && section.code && (
          <div className="border border-jax-navy rounded-lg p-3 sm:p-4 space-y-3 bg-jax-bg">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="space-y-1.5 sm:w-28">
                <label className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase">Language</label>
                <select
                  value={section.code.language}
                  onChange={(e) => setCode({ language: e.target.value })}
                  className="w-full bg-jax-surface border border-jax-navy rounded px-2 py-2 text-[11px] sm:text-[12px] font-mono text-white outline-none focus:border-jax-link"
                >
                  {CODE_LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="flex-1 space-y-1.5 min-w-0">
                <label className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase">Filename (optional)</label>
                <input
                  type="text"
                  value={section.code.label ?? ''}
                  onChange={(e) => setCode({ label: e.target.value })}
                  placeholder="e.g. Button.tsx"
                  className="w-full bg-jax-surface border border-jax-navy rounded-lg px-3 py-2 text-[11px] sm:text-[13px] font-mono text-white outline-none focus:border-jax-link transition-colors"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase">Code</label>
              <AutoResizeTextarea
                value={section.code.code}
                onChange={(e) => setCode({ code: e.target.value })}
                placeholder="// your code here"
                className="w-full min-w-0 bg-jax-surface border border-jax-navy rounded-lg px-3 py-2.5 text-[10px] sm:text-[13px] font-mono text-white leading-relaxed outline-none focus:border-jax-link transition-colors min-h-[6rem]"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Note / callout toggle */}
      <div className="space-y-3">
        <button onClick={toggleNote}
          className={`flex items-center gap-1.5 text-[11px] font-mono transition-colors ${noteOpen ? 'text-jax-link' : 'text-jax-subtle hover:text-jax-link'}`}>
          <PencilLine className="w-3 h-3" />
          {noteOpen ? 'remove callout note' : '+ add callout note'}
        </button>
        {noteOpen && (
          <AutoResizeTextarea
            value={section.note ?? ''}
            onChange={(e) => onChange({ ...section, note: e.target.value })}
            placeholder="Key distinction, gotcha, or something easy to mix up..."
            className="w-full min-w-0 bg-jax-bg border border-jax-link/40 rounded-lg px-3 py-2.5 text-[10px] sm:text-sm text-jax-subtle leading-relaxed outline-none focus:border-jax-link transition-colors font-mono sm:text-[13px]"
          />
        )}
      </div>
    </div>
  )
}

// ─── PIN modal ────────────────────────────────────────────────────────────────

const PinModal: React.FC<{
  onVerified: (pin: string, onError: (msg: string) => void) => void
  onCancel: () => void
}> = ({ onVerified, onCancel }) => {
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => { inputRefs.current[0]?.focus() }, [])

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus()
    }
  }

  const onPinError = (msg: string) => {
    setChecking(false)
    setError(msg)
    setDigits(['', '', '', '', '', ''])
    setTimeout(() => inputRefs.current[0]?.focus(), 50)
  }

  const handleChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[i] = digit
    setDigits(next)
    setError('')
    if (digit && i < 5) inputRefs.current[i + 1]?.focus()
    if (digit && i === 5) {
      const pin = next.join('')
      if (pin.length === 6) submit(pin)
    }
  }

  const submit = (pin: string) => {
    if (checking) return
    setChecking(true)
    setError('')
    onVerified(pin, onPinError)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const pin = digits.join('')
    if (pin.length === 6) submit(pin)
  }

  return (
    // Backdrop
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-jax-surface border border-jax-navy rounded-2xl p-8 w-full max-w-sm mx-4 shadow-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-jax-bg border border-jax-navy flex items-center justify-center">
            <Lock className="w-5 h-5 text-jax-link" />
          </div>
        </div>

        <h2 className="text-white font-semibold text-center text-lg mb-1 tracking-tight">
          Enter PIN to publish
        </h2>
        <p className="text-jax-subtle text-[12px] font-mono text-center mb-8">
          6-digit admin PIN required
        </p>

        <form onSubmit={handleSubmit}>
          {/* 6 digit inputs */}
          <div className="flex justify-center gap-2 mb-6">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKey(i, e)}
                className={`w-10 h-12 text-center text-lg font-mono font-bold text-white bg-jax-bg border rounded-lg outline-none transition-colors duration-150 ${
                  error ? 'border-red-500' : d ? 'border-jax-link' : 'border-jax-navy focus:border-jax-link'
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="text-red-400 text-[11px] font-mono text-center mb-4">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2.5 border border-jax-navy rounded-lg text-[12px] font-mono text-jax-subtle hover:text-white hover:border-white transition-colors"
            >
              cancel
            </button>
            <button
              type="submit"
              disabled={digits.join('').length < 6 || checking}
              className="flex-1 py-2.5 bg-jax-link text-jax-bg rounded-lg text-[12px] font-mono font-bold tracking-widest uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {checking ? 'checking...' : 'publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Publish bar ──────────────────────────────────────────────────────────────

const PublishBar: React.FC<{
  post: Post
  isUpdate: boolean
  status: PushStatus
  message: string
  onRequestPublish: () => void
}> = ({ post, isUpdate, status, message, onRequestPublish }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-jax-navy bg-jax-surface rounded-lg px-4 sm:px-5 py-4">
    <div className="min-w-0">
      <p className="text-white text-xs sm:text-sm font-semibold line-clamp-2 break-words">{post.title}</p>
      <p className="text-jax-subtle text-[10px] sm:text-[11px] font-mono mt-0.5 truncate">/writing/{post.slug}</p>
    </div>
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {status === 'success' && (
        <span className="text-[10px] sm:text-[11px] font-mono text-emerald-400 flex items-center gap-1">
          <CheckCircle className="w-3.5 h-3.5 shrink-0" /> {message}
        </span>
      )}
      {status === 'error' && (
        <span className="text-[10px] sm:text-[11px] font-mono text-red-400 flex items-center gap-1 max-w-full sm:max-w-[200px] text-left sm:text-right">
          <XCircle className="w-3.5 h-3.5 shrink-0" /> {message}
        </span>
      )}
      <button
        onClick={onRequestPublish}
        disabled={status === 'pushing'}
        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-jax-link text-jax-bg rounded font-mono text-[10px] sm:text-[11px] font-bold tracking-widest uppercase hover:opacity-90 transition-opacity disabled:opacity-50 w-full sm:w-auto"
      >
        <Upload className="w-3 h-3" />
        {status === 'pushing' ? 'pushing...' : isUpdate ? 'update' : 'publish'}
      </button>
    </div>
  </div>
)

// ─── Prompt tab (isolated so copy state doesn't re-render the world) ──────────

const PromptTab: React.FC = () => {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(PROMPT_TEMPLATE).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="space-y-4">
      <p className="text-jax-body text-sm leading-relaxed">
        Copy this prompt, paste it into your AI, and tell it what the post is about. It returns JSON you paste into the JSON tab.
      </p>
      <div className="relative">
        <pre className="bg-jax-surface border border-jax-navy rounded-lg p-5 text-[12px] font-mono text-jax-body leading-relaxed whitespace-pre-wrap overflow-x-auto">
          {PROMPT_TEMPLATE}
        </pre>
        <button
          onClick={copy}
          className={`absolute top-3 right-3 text-[10px] font-mono border rounded px-2 py-1 bg-jax-bg transition-colors ${
            copied
              ? 'border-emerald-800 text-emerald-400'
              : 'border-jax-navy text-jax-subtle hover:text-jax-link'
          }`}
        >
          {copied ? 'copied!' : 'copy'}
        </button>
      </div>
      <p className="text-jax-subtle text-[11px] font-mono">
        Paste the output into JSON tab, then use the Edit tab to tweak without touching raw JSON.
      </p>
    </div>
  )
}

type PushStatus = 'idle' | 'pushing' | 'success' | 'error'
type Tab = 'posts' | 'prompt' | 'json' | 'edit' | 'preview'

interface PostMeta {
  slug: string
  title: string
  date: string
  read_time: string
  published: boolean
}

// ─── Main component ───────────────────────────────────────────────────────────

const PostEditor: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>('posts')

  useEffect(() => {
    const prev = document.title
    document.title = 'Post Editor'
    return () => { document.title = prev }
  }, [])
  const [raw, setRaw] = useState('')
  const [parsed, setParsed] = useState<Post | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [pushStatus, setPushStatus] = useState<PushStatus>('idle')
  const [pushMessage, setPushMessage] = useState('')
  const [showPin, setShowPin] = useState(false)

  // ── Published posts list ───────────────────────────────────────────────────
  const [postList, setPostList] = useState<PostMeta[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)

  useEffect(() => {
    supabase
      .from('posts')
      .select('slug, title, date, read_time, published')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setPostList((data as PostMeta[]) ?? [])
        setListLoading(false)
      })
  }, [pushStatus]) // re-fetch after a publish

  // Handle ?tab= and ?slug= query params from double-click backdoor
  const autoLoaded = useRef(false)
  useEffect(() => {
    if (autoLoaded.current) return
    autoLoaded.current = true

    const slugParam = searchParams.get('slug')
    const tabParam = searchParams.get('tab') as Tab | null

    if (slugParam) {
      // Load a specific post and go to edit tab
      loadPost(slugParam)
    } else if (tabParam) {
      // Just switch to the requested tab
      setTab(tabParam)
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadPost = async (slug: string) => {
    setLoadingSlug(slug)
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .eq('slug', slug)
      .single()
    setLoadingSlug(null)
    if (error || !data) return

    // Map DB columns → Post shape
    const post: Post = {
      slug: data.slug,
      title: data.title,
      subtitle: data.subtitle ?? '',
      date: data.date,
      readTime: data.read_time,
      intro: data.intro ?? '',
      sections: data.sections ?? [],
      closing: data.closing ?? '',
    }
    setParsed(post)
    setRaw(JSON.stringify(post, null, 2))
    setParseError(null)
    setPushStatus('idle')
    setPushMessage('')
    setIsUpdate(true)
    setTab('edit')
  }

  // ── Parse raw JSON → parsed post ──────────────────────────────────────────
  const parseRaw = useCallback((text: string) => {
    setRaw(text)
    if (!text.trim()) { setParsed(null); setParseError(null); return }
    try {
      const obj = JSON.parse(text)
      if (!obj.slug || !obj.title || !obj.sections) throw new Error('Missing required fields: slug, title, sections')
      if (!Array.isArray(obj.sections)) throw new Error('"sections" must be an array')
      setParsed(obj as Post)
      setParseError(null)
      // Check if this slug already exists in the list (pasting an existing post's JSON)
      setIsUpdate(postList.some((p) => p.slug === obj.slug))
    } catch (e) {
      setParsed(null)
      setParseError((e as Error).message)
    }
  }, [postList])

  // ── Update parsed post → keep raw in sync ─────────────────────────────────
  const updatePost = useCallback((next: Post) => {
    setParsed(next)
    setRaw(JSON.stringify(next, null, 2))
  }, [])

  // ── Section helpers ────────────────────────────────────────────────────────
  const updateSection = (i: number, s: PostSection) => {
    if (!parsed) return
    const next = [...parsed.sections]
    next[i] = s
    updatePost({ ...parsed, sections: next })
  }

  const removeSection = (i: number) => {
    if (!parsed) return
    updatePost({ ...parsed, sections: parsed.sections.filter((_, j) => j !== i) })
  }

  const moveSection = (i: number, dir: -1 | 1) => {
    if (!parsed) return
    const next = [...parsed.sections]
    const swap = next[i + dir]
    next[i + dir] = next[i]
    next[i] = swap
    updatePost({ ...parsed, sections: next })
  }

  const addSection = () => {
    if (!parsed) return
    updatePost({
      ...parsed,
      sections: [...parsed.sections, { heading: '', body: [''] }],
    })
  }

  // ── Publish — edge function verifies PIN + writes with service role ─────────
  const push = async (pin: string, onPinError: (msg: string) => void) => {
    if (!parsed) return
    setPushStatus('pushing')
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/publish-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, post: parsed }),
      })
      const data = await res.json()

      if (res.status === 403 || data.verified === false) {
        // Wrong PIN — keep modal open, surface error there
        onPinError('Wrong PIN. Try again.')
        setPushStatus('idle')
        return
      }

      setShowPin(false)

      if (!res.ok || data.error) {
        setPushStatus('error')
        setPushMessage(data.error ?? 'Publish failed')
      } else {
        setPushStatus('success')
        setPushMessage(`Live at /writing/${parsed.slug}`)
      }
    } catch (e) {
      setShowPin(false)
      setPushStatus('error')
      setPushMessage((e as Error).message)
    }
  }

  const requestPublish = () => {
    setPushStatus('idle')
    setPushMessage('')
    setShowPin(true)
  }

  const [isUpdate, setIsUpdate] = useState(false)

  const tabClass = (t: Tab) =>
    `px-4 py-2.5 text-[11px] font-mono tracking-widest uppercase transition-colors duration-150 border-b-2 whitespace-nowrap ${
      tab === t ? 'border-jax-link text-jax-link' : 'border-transparent text-jax-subtle hover:text-white'
    }`

  const noPost = (msg = 'Paste valid JSON in the JSON tab first.') => (
    <p className="text-jax-subtle font-mono text-sm">{msg}</p>
  )

  return (
    <div className="min-h-screen bg-jax-bg text-white" style={{ fontFamily: '"Source Sans Pro", system-ui, sans-serif' }}>
      {showPin && (
        <PinModal
          onVerified={(pin, onPinError) => push(pin, onPinError)}
          onCancel={() => setShowPin(false)}
        />
      )}
      <div className="h-[2px] bg-jax-accent" />

      <header className="border-b border-jax-navy">
        <nav className="max-w-[900px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/about" className="inline-flex items-center gap-2 font-mono text-[11px] text-jax-subtle hover:text-jax-link transition-colors duration-200">
            <ArrowLeft className="w-3 h-3" /> back
          </Link>
          <span className="font-mono text-[11px] text-jax-subtle tracking-widest uppercase">post editor</span>
        </nav>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 pb-24">

        {/* Tabs */}
        <div className="flex gap-1 mt-8 mb-8 border-b border-jax-navy overflow-x-auto">
          <button className={tabClass('posts')} onClick={() => setTab('posts')}>
            <span className="flex items-center gap-1.5"><FileText className="w-3 h-3" />Posts</span>
          </button>
          <button className={tabClass('prompt')} onClick={() => setTab('prompt')}>
            <span className="flex items-center gap-1.5"><Code2 className="w-3 h-3" />AI Prompt</span>
          </button>
          <button className={tabClass('json')} onClick={() => setTab('json')}>
            <span className="flex items-center gap-1.5">
              <Code2 className="w-3 h-3" />JSON
              {parseError && <XCircle className="w-3 h-3 text-red-400" />}
              {parsed && !parseError && <CheckCircle className="w-3 h-3 text-emerald-400" />}
            </span>
          </button>
          <button className={tabClass('edit')} onClick={() => setTab('edit')} disabled={!parsed}>
            <span className={`flex items-center gap-1.5 ${!parsed ? 'opacity-30' : ''}`}>
              <PencilLine className="w-3 h-3" />Edit
            </span>
          </button>
          <button className={tabClass('preview')} onClick={() => setTab('preview')} disabled={!parsed}>
            <span className={`flex items-center gap-1.5 ${!parsed ? 'opacity-30' : ''}`}>
              <Eye className="w-3 h-3" />Preview
            </span>
          </button>
        </div>

        {/* ── Posts tab ─────────────────────────────────────────────────── */}
        {tab === 'posts' && (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <p className="text-jax-body text-xs sm:text-sm">Click any post to load it into the editor.</p>
              <button
                onClick={() => { setParsed(null); setRaw(''); setParseError(null); setPushStatus('idle'); setIsUpdate(false); setTab('prompt') }}
                className="flex items-center justify-center gap-1.5 text-[11px] font-mono text-jax-subtle hover:text-jax-link border border-jax-navy rounded px-3 py-2.5 sm:py-1.5 transition-colors w-full sm:w-auto"
              >
                <Plus className="w-3 h-3" /> new post
              </button>
            </div>

            {listLoading ? (
              <div className="flex items-center gap-2 text-jax-subtle font-mono text-sm py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> loading posts...
              </div>
            ) : postList.length === 0 ? (
              <p className="text-jax-subtle font-mono text-xs sm:text-sm py-8 text-center">No posts yet. Create one using the AI Prompt tab.</p>
            ) : (
              postList.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => loadPost(p.slug)}
                  disabled={loadingSlug === p.slug}
                  className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 border border-jax-navy bg-jax-surface rounded-xl px-4 sm:px-5 py-4 hover:border-jax-link transition-colors duration-150 text-left group disabled:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-xs sm:text-sm font-semibold line-clamp-2 break-words group-hover:text-jax-link transition-colors">
                      {p.title}
                    </p>
                    <p className="text-jax-subtle text-[10px] sm:text-[11px] font-mono mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="truncate">/writing/{p.slug}</span>
                      <span className="opacity-40 hidden sm:inline">·</span>
                      <span>{p.date}</span>
                      <span className="opacity-40 hidden sm:inline">·</span>
                      <span className={p.published ? 'text-emerald-400' : 'text-yellow-500'}>
                        {p.published ? '● live' : '○ draft'}
                      </span>
                    </p>
                  </div>
                  <div className="shrink-0 self-start sm:self-center sm:ml-0">
                    {loadingSlug === p.slug ? (
                      <Loader2 className="w-4 h-4 text-jax-subtle animate-spin" />
                    ) : (
                      <PencilRuler className="hidden sm:block w-4 h-4 text-jax-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* ── Prompt tab ────────────────────────────────────────────────── */}
        {tab === 'prompt' && (
          <PromptTab />
        )}

        {/* ── JSON tab ──────────────────────────────────────────────────── */}
        {tab === 'json' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-jax-body text-sm">Paste the AI output here.</p>
              {parsed && !parseError && (
                <span className="flex items-center gap-1.5 text-[11px] font-mono text-emerald-400">
                  <CheckCircle className="w-3.5 h-3.5" /> valid — switch to Edit tab to refine
                </span>
              )}
              {parseError && (
                <span className="flex items-center gap-1.5 text-[11px] font-mono text-red-400">
                  <XCircle className="w-3.5 h-3.5" /> {parseError}
                </span>
              )}
            </div>
            <textarea
              value={raw}
              onChange={(e) => parseRaw(e.target.value)}
              placeholder={`{\n  "slug": "my-post",\n  "title": "...",\n  ...\n}`}
              className="w-full min-h-[260px] sm:h-[520px] bg-jax-surface border border-jax-navy rounded-lg p-3 sm:p-4 text-[11px] sm:text-[13px] font-mono text-jax-body leading-relaxed resize-y sm:resize-none outline-none focus:border-jax-link transition-colors duration-150"
              spellCheck={false}
            />
            {parsed && !parseError && <PublishBar post={parsed} isUpdate={isUpdate} status={pushStatus} message={pushMessage} onRequestPublish={requestPublish} />}
          </div>
        )}

        {/* ── Edit tab ──────────────────────────────────────────────────── */}
        {tab === 'edit' && (
          !parsed ? noPost() : (
            <div className="space-y-8 min-w-0 overflow-x-hidden">

              {/* Meta fields */}
              <div className="border border-jax-navy rounded-xl bg-jax-surface p-4 sm:p-5 space-y-4 min-w-0">
                <span className="block text-[10px] font-mono text-jax-subtle tracking-widest uppercase mb-1">Post info</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 min-w-0">
                  <Field label="Title" value={parsed.title} onChange={(v) => updatePost({ ...parsed, title: v })} placeholder="Post title" />
                  <Field label="Slug (URL)" value={parsed.slug} mono onChange={(v) => updatePost({ ...parsed, slug: v })} placeholder="my-post-slug" />
                  <Field label="Date" value={parsed.date} onChange={(v) => updatePost({ ...parsed, date: v })} placeholder="Mar 2026" />
                  <Field label="Read time" value={parsed.readTime} onChange={(v) => updatePost({ ...parsed, readTime: v })} placeholder="5 min" />
                </div>
                <Field
                  label="Subtitle"
                  value={parsed.subtitle ?? ''}
                  onChange={(v) => updatePost({ ...parsed, subtitle: v })}
                  placeholder="One sentence describing the post"
                  multiline
                  rows={2}
                  autoResize
                />
                <Field
                  label="Intro"
                  value={parsed.intro}
                  onChange={(v) => updatePost({ ...parsed, intro: v })}
                  multiline rows={2}
                  placeholder="Opening paragraph — what confused you, what you figured out"
                  autoResize
                />
              </div>

              {/* Sections */}
              <div className="space-y-4 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-jax-subtle tracking-widest uppercase">Sections</span>
                  <span className="text-[11px] font-mono text-jax-subtle">{parsed.sections.length} total</span>
                </div>
                {parsed.sections.map((section, i) => (
                  <SectionEditor
                    key={i}
                    section={section}
                    index={i}
                    total={parsed.sections.length}
                    onChange={(s) => updateSection(i, s)}
                    onRemove={() => removeSection(i)}
                    onMoveUp={() => moveSection(i, -1)}
                    onMoveDown={() => moveSection(i, 1)}
                  />
                ))}
                <button
                  onClick={addSection}
                  className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-jax-navy rounded-xl text-[11px] font-mono text-jax-subtle hover:text-jax-link hover:border-jax-link transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> add section
                </button>
              </div>

              {/* Closing */}
              <div className="border border-jax-navy rounded-xl bg-jax-surface p-4 sm:p-5">
                <Field
                  label="Closing"
                  value={parsed.closing ?? ''}
                  onChange={(v) => updatePost({ ...parsed, closing: v })}
                  multiline rows={2}
                  placeholder="The thing that ties it together — the insight that makes the whole post make sense"
                  autoResize
                />
              </div>

              <PublishBar post={parsed} isUpdate={isUpdate} status={pushStatus} message={pushMessage} onRequestPublish={requestPublish} />
            </div>
          )
        )}

        {/* ── Preview tab ───────────────────────────────────────────────── */}
        {tab === 'preview' && (
          !parsed ? noPost() : (
            <div>
              <section className="pb-10 border-b border-jax-navy">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-[11px] font-mono text-jax-subtle">{parsed.date}</span>
                  <span className="text-[11px] font-mono text-jax-subtle">{parsed.readTime} read</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight mb-4">
                  {parsed.title}
                </h1>
                <p className="text-jax-link font-mono text-sm">{parsed.subtitle}</p>
              </section>

              <PostRenderer post={parsed} />

              <div className="mt-12 border-t border-jax-navy pt-8">
                <PublishBar post={parsed} isUpdate={isUpdate} status={pushStatus} message={pushMessage} onRequestPublish={requestPublish} />
              </div>
            </div>
          )
        )}

      </main>
    </div>
  )
}

export default PostEditor
